import React, {
	type KeyboardEvent,
	type MouseEvent,
	useCallback,
	useState,
	useRef,
	useEffect,
	type ChangeEvent,
	type Ref,
} from 'react';

import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/glyph/add';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import {
	HEADER_DROPDOWN_X2C_NUDGE_STEP,
	useX2CNudgeStep,
} from '@atlassian/jira-project-create-importer-onboarding';
import { BlockerIcon } from '../../../../common/assets/blocker-icon';
import { COLUMN_HEADER_TEST_ID_PREFIX } from '../../../../common/constants';
import {
	type Field,
	type CustomFieldType,
	supportedCustomFieldTypes,
} from '../../../../common/constants/fields';
import type { ColumnName } from '../../../../common/types';
import { SearchBar } from '../../../../common/ui/search-bar';
import { useGetValidationErrorMessage } from '../../../../common/utils';
import {
	getDescriptorForNewFieldType,
	getIcon,
	ICON_COLOR_INVALID,
} from '../../../../common/utils/fields';
import { useMappingState } from '../../../../controllers/import-state';

import messages from './messages';
import { NoFieldsAvailable } from './no-fields-available';
import { SelectedField } from './selected-field';
import { getValidationError } from './utils';

type Props = {
	name: ColumnName;
	field?: Field | null;
	isFirstColumn: boolean;
};

const CUSTOM_FIELD_INFO_LINK =
	'https://confluence.atlassian.com/adminjiraserver/adding-custom-fields-1047552713.html';

export const ColumnHeader = ({ name, field, isFirstColumn }: Props) => {
	const [mappingState, { setIsVisibleForColumn, setFieldForColumn }] = useMappingState();
	const { formatMessage } = useIntl();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [isOpen, setIsOpen] = useState(false);
	const [isCustomFieldsMenuOpen, setIsCustomFieldsMenuOpen] = useState(false);
	const columnHeaderButtonRef = useRef<HTMLElement>();
	const { getValidationErrorMessage } = useGetValidationErrorMessage();
	const [{ nudgeStep }] = useX2CNudgeStep();
	const shouldShowDropdownSpotlight = isFirstColumn && nudgeStep === HEADER_DROPDOWN_X2C_NUDGE_STEP;

	useEffect(() => {
		if (mappingState?.selectedColumn === name) {
			columnHeaderButtonRef.current?.focus();
		}
	}, [mappingState?.selectedColumn, name]);

	const updateColumnHeaderButtonRefAndForwardTriggerRef = useCallback(
		(triggerRef: Ref<HTMLElement>) => (element: HTMLElement) => {
			columnHeaderButtonRef.current = element;

			typeof triggerRef === 'function' && triggerRef(element);
		},
		[],
	);

	const validationError = getValidationError(mappingState, name);

	const getSetSystemFieldCallback = useCallback(
		(newField: Field) => () => {
			setFieldForColumn(name, newField);
		},
		[name, setFieldForColumn],
	);

	const toggleMenuOpenState = useCallback((attrs: OnOpenChangeArgs) => {
		setIsOpen(attrs.isOpen);

		if (!attrs.isOpen) {
			// Reset search if menu closed
			setSearchTerm('');
			setIsCustomFieldsMenuOpen(false);
		}
	}, []);

	const getSetCustomFieldCallback = useCallback(
		(customFieldType: CustomFieldType) => () => {
			setFieldForColumn(name, {
				fieldName: name, // Default field name is the column name for new fields,
				fieldType: customFieldType,
			});
		},
		[name, setFieldForColumn],
	);

	const removeFieldHandler = useCallback(() => {
		setIsVisibleForColumn(name, false);
	}, [name, setIsVisibleForColumn]);

	const onSearchTermChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
		[],
	);

	const matchesSearchTerm = useCallback(
		(fieldToCheck: Field) =>
			searchTerm === '' || fieldToCheck.fieldName.toLowerCase().includes(searchTerm.toLowerCase()),
		[searchTerm],
	);

	const onLearnMoreClicked = useCallback((event: MouseEvent<Element> | KeyboardEvent<Element>) => {
		event.preventDefault();
		event.stopPropagation();
		window.open(CUSTOM_FIELD_INFO_LINK, '_blank');
	}, []);

	const onLearnMoreKeyDown = useCallback(
		(event: KeyboardEvent<Element>) => {
			if (event.key === 'Enter') {
				onLearnMoreClicked(event);
			}
		},
		[onLearnMoreClicked],
	);

	const onClickSelectCustomField = useCallback(
		(event: MouseEvent<Element> | KeyboardEvent<Element>) => {
			event.stopPropagation();
			setIsCustomFieldsMenuOpen(true);
		},
		[],
	);

	const onClickGoBack = useCallback((event: MouseEvent<Element> | KeyboardEvent<Element>) => {
		event.stopPropagation();
		setIsCustomFieldsMenuOpen(false);
	}, []);

	const getSelectedCustomField = useCallback(() => {
		if (!field || field.fieldId) return null;

		return (
			<SelectedField
				icon={getIcon({ fieldType: field.fieldType })}
				name={formatMessage(getDescriptorForNewFieldType(field.fieldType))}
			/>
		);
	}, [field, formatMessage]);

	const createDropDownItems = useCallback(
		(availableFieldItems: Field[], filterCondition: (fieldToCheck: Field) => boolean) =>
			mappingState
				? availableFieldItems
						.filter(matchesSearchTerm)
						.filter(filterCondition)
						.map((fieldItem) => {
							const isDisabled =
								!!fieldItem.fieldId && mappingState.mappedJiraFieldIds.has(fieldItem.fieldId);
							return (
								<DropdownItem
									key={`${fieldItem.fieldName}-${fieldItem.fieldType}`}
									onClick={getSetSystemFieldCallback(fieldItem)} // setFieldForColumn
									elemBefore={getIcon({
										fieldType: fieldItem.fieldType,
										isDisabled,
									})}
									isDisabled={isDisabled}
									testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-item.${fieldItem.fieldName}-${fieldItem.fieldType}`}
								>
									{fieldItem.fieldName}
								</DropdownItem>
							);
						})
				: [],
		[matchesSearchTerm, mappingState, getSetSystemFieldCallback],
	);

	const getExistingFieldsSection = useCallback(() => {
		const isIssueLinkFieldsAvailable = ff('x2c-issuelinking-frontend_tuyzk');
		if (mappingState === null) {
			return null;
		}

		const availableJiraFieldItems = isIssueLinkFieldsAvailable
			? createDropDownItems(
					mappingState.availableJiraFields,
					(fieldToCheck: Field) => !(!!field?.fieldId && fieldToCheck.fieldId === field.fieldId),
				)
			: mappingState.availableJiraFields
					.filter(matchesSearchTerm)
					.sort((jiraField) => (!!field?.fieldId && jiraField.fieldId === field.fieldId ? -1 : 0))
					.map((jiraField) => {
						const isSelected = !!field?.fieldId && jiraField.fieldId === field.fieldId;
						const isDisabled =
							!!jiraField.fieldId && mappingState.mappedJiraFieldIds.has(jiraField.fieldId);
						return isSelected ? (
							<SelectedField
								key={jiraField.fieldType}
								icon={getIcon({ fieldType: jiraField.fieldType })}
								name={jiraField.fieldName}
							/>
						) : (
							<DropdownItem
								key={`${jiraField.fieldName}-${jiraField.fieldType}`}
								onClick={getSetSystemFieldCallback(jiraField)}
								elemBefore={getIcon({ fieldType: jiraField.fieldType, isDisabled })}
								isDisabled={isDisabled}
								testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-item.${jiraField.fieldName}-${jiraField.fieldType}`}
							>
								{jiraField.fieldName}
							</DropdownItem>
						);
					});

		const availableIssueLinkFieldItems = isIssueLinkFieldsAvailable
			? createDropDownItems(
					mappingState.availableIssueLinkFields,
					(fieldToCheck: Field) =>
						!(!!field?.fieldName && fieldToCheck.fieldName === field.fieldName),
				)
			: [];

		const DropDownItems = isIssueLinkFieldsAvailable ? (
			<>
				<SearchBarWrapper>
					<SearchBar onSearchTermChange={onSearchTermChange} />
				</SearchBarWrapper>
				{field && field.fieldId && (
					<SelectedField
						key={field.fieldType}
						icon={getIcon({ fieldType: field.fieldType })}
						name={field.fieldName}
					/>
				)}
				<SectionWrapper>
					<SectionHeader level="h300" as="h3">
						{formatMessage(messages.existingFieldSectionHeading)}
					</SectionHeader>
				</SectionWrapper>
				{availableJiraFieldItems.length === 0 ? (
					<NoFieldsAvailable />
				) : (
					<>{availableJiraFieldItems}</>
				)}
				{mappingState.availableIssueLinkFields.length !== 0 && (
					<SectionWrapper>
						<SectionHeader level="h300" as="h3">
							{formatMessage(messages.issueLinksFieldSectionHeading)}
						</SectionHeader>
					</SectionWrapper>
				)}
				{mappingState.availableIssueLinkFields.length !== 0 &&
					(availableIssueLinkFieldItems.length === 0 ? (
						<NoFieldsAvailable />
					) : (
						<>{availableIssueLinkFieldItems}</>
					))}
			</>
		) : (
			<>
				<SectionWrapper>
					<SectionHeader level="h300" as="h3">
						{formatMessage(messages.existingFieldSectionHeading)}
					</SectionHeader>
				</SectionWrapper>
				<SearchBarWrapper>
					<SearchBar onSearchTermChange={onSearchTermChange} />
				</SearchBarWrapper>
				{availableJiraFieldItems.length === 0 ? (
					<NoFieldsAvailable />
				) : (
					<>{availableJiraFieldItems}</>
				)}
			</>
		);

		return (
			<>
				<Box xcss={removeFieldWrapperStyles}>
					<DropdownItem
						onClick={removeFieldHandler}
						elemBefore={<BlockerIcon />}
						testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.remove-field`}
					>
						{formatMessage(messages.removeField)}
					</DropdownItem>
				</Box>
				<SectionDivider />
				{mappingState.availableNewFieldTypes.length !== 0 && (
					<>
						<DropdownItem
							testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.custom-field`}
							onClick={onClickSelectCustomField}
							elemAfter={<ChevronRightIcon label="" />}
						>
							<SectionHeader level="h300" as="h3">
								{formatMessage(messages.setNewFieldTypeHeading)}
							</SectionHeader>
							<SectionDescription>
								{formatMessage(messages.setNewFieldTypeDescription)}{' '}
								<LearnMoreLink
									data-testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.learn-more-link`}
									onClick={onLearnMoreClicked}
									onKeyDown={onLearnMoreKeyDown}
									tabIndex={0}
								>
									{formatMessage(messages.learnMoreLink)}
								</LearnMoreLink>
							</SectionDescription>
						</DropdownItem>
						{getSelectedCustomField()}
						<SectionDivider />
					</>
				)}
				<DropdownItemGroup
					testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-item-group-existing-field`}
				>
					{DropDownItems}
				</DropdownItemGroup>
			</>
		);
	}, [
		field,
		formatMessage,
		getSelectedCustomField,
		getSetSystemFieldCallback,
		mappingState,
		matchesSearchTerm,
		onClickSelectCustomField,
		onLearnMoreClicked,
		onLearnMoreKeyDown,
		onSearchTermChange,
		removeFieldHandler,
		createDropDownItems,
	]);

	const getCustomFieldsSection = useCallback(() => {
		if (mappingState === null) {
			return null;
		}

		const availableNewFieldItems = mappingState.availableNewFieldTypes
			.filter((fieldItem) => supportedCustomFieldTypes.has(fieldItem))
			.map((newFieldType) => ({
				fieldType: newFieldType,
				fieldName: formatMessage(getDescriptorForNewFieldType(newFieldType)),
			}))
			.map((newField) => {
				const isSelected = !!field && !field.fieldId && newField.fieldType === field.fieldType;

				return isSelected ? (
					<SelectedField
						key={newField.fieldType}
						icon={getIcon({ fieldType: newField.fieldType })}
						name={formatMessage(getDescriptorForNewFieldType(newField.fieldType))}
					/>
				) : (
					<DropdownItem
						key={newField.fieldType}
						onClick={getSetCustomFieldCallback(newField.fieldType)}
						elemBefore={getIcon({ fieldType: newField.fieldType })}
						testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-item.${newField.fieldName}`}
					>
						{newField.fieldName}
					</DropdownItem>
				);
			});

		return (
			<DropdownItemGroup
				testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-item-group-custom-field`}
			>
				<GoBackWrapper>
					<DropdownItem
						elemBefore={<ArrowLeftIcon label="" />}
						onClick={onClickGoBack}
						testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.go-back-wrapper`}
					>
						{formatMessage(messages.goBack)}
					</DropdownItem>
				</GoBackWrapper>
				<SectionWrapper>
					<SectionHeader level="h300" as="h3">
						{formatMessage(messages.selectCustomFieldHeading)}
					</SectionHeader>
					<SectionDescription>
						{formatMessage(messages.selectCustomFieldDescription)}
					</SectionDescription>
				</SectionWrapper>
				{availableNewFieldItems.length === 0 ? (
					<NoFieldsAvailable />
				) : (
					<>{availableNewFieldItems}</>
				)}
			</DropdownItemGroup>
		);
	}, [field, formatMessage, getSetCustomFieldCallback, mappingState, onClickGoBack]);

	const getEmptyFieldTrigger = useCallback(
		() => (
			<EmptyFieldWrapper>
				<AddIcon label="" testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.add-icon`} />
				<span>{formatMessage(messages.mapToAField)}</span>
			</EmptyFieldWrapper>
		),
		[formatMessage],
	);

	const getFieldSelectedTrigger = useCallback(
		({ fieldType, fieldName, fieldId }: Field) => {
			const hasValidationError = Boolean(validationError);

			return (
				<>
					<ContentLeftWrapper>
						{getIcon({ fieldType, hasValidationError })}
						<Header as="h3" level="h500" hasValidationError={hasValidationError}>
							<Tooltip
								content={fieldName}
								position="bottom-start"
								testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.trigger-tooltip`}
							>
								<span>{fieldName}</span>
							</Tooltip>
						</Header>
					</ContentLeftWrapper>
					<ContentRightWrapper>
						{!fieldId && (
							<Lozenge appearance="new" testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.lozenge-new`}>
								{formatMessage(messages.newField)}
							</Lozenge>
						)}
						{validationError && (
							<Tooltip
								content={getValidationErrorMessage(validationError)}
								testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.error-tooltip`}
							>
								<ErrorIcon
									label=""
									size="medium"
									primaryColor={ICON_COLOR_INVALID}
									testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.error-icon`}
								/>
							</Tooltip>
						)}
					</ContentRightWrapper>
				</>
			);
		},
		[formatMessage, getValidationErrorMessage, validationError],
	);

	const dropdownTrigger = useCallback(
		({ triggerRef, isSelected, ...props }: CustomTriggerProps<HTMLElement>) => (
			<StyledButton
				{...props}
				iconAfter={field ? <ChevronDownIcon label="dropdown icon" size="large" /> : undefined}
				ref={updateColumnHeaderButtonRefAndForwardTriggerRef(triggerRef)}
				appearance="subtle"
			>
				{field ? getFieldSelectedTrigger(field) : getEmptyFieldTrigger()}
			</StyledButton>
		),
		[
			field,
			getEmptyFieldTrigger,
			getFieldSelectedTrigger,
			updateColumnHeaderButtonRefAndForwardTriggerRef,
		],
	);

	const onKeyDownHandler = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
		}
	}, []);

	return (
		<Wrapper data-testid={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-menu`}>
			<DropdownMenu
				testId={`${COLUMN_HEADER_TEST_ID_PREFIX}.dropdown-menu-${name}`}
				isOpen={isOpen || shouldShowDropdownSpotlight}
				onOpenChange={toggleMenuOpenState}
				shouldFlip
				trigger={dropdownTrigger}
			>
				<ConditionalWrapper
					condition={shouldShowDropdownSpotlight}
					renderWrapper={(children) => (
						<SpotlightTarget name={HEADER_DROPDOWN_X2C_NUDGE_STEP}>
							<SpotlightWrapper>{children}</SpotlightWrapper>
						</SpotlightTarget>
					)}
				>
					<DropdownItemWrapper onKeyDown={onKeyDownHandler}>
						{isCustomFieldsMenuOpen ? getCustomFieldsSection() : getExistingFieldsSection()}
					</DropdownItemWrapper>
				</ConditionalWrapper>
			</DropdownMenu>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	boxSizing: 'border-box',
	whiteSpace: 'nowrap',
	width: '100%',
	minWidth: 0,
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		alignItems: 'center',
		padding: '0px',
		width: '100%',
		minWidth: 0,
		height: '100%',
		borderRadius: '0px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			display: 'flex',
		},
	},
});

type HeaderProps = { hasValidationError: boolean };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled(Heading)<HeaderProps>({
	fontSize: '14px',
	fontWeight: 600,
	lineHeight: '16px',
	margin: `0px ${token('space.050', '4px')}`,
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.hasValidationError
			? token('color.text.danger', '#AE2A19')
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text', colors.N900),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentLeftWrapper = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-start',
	marginLeft: token('space.100', '8px'),
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentRightWrapper = styled.div({
	display: 'flex',
	flex: '0 1 auto',
	flexDirection: 'row',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		height: token('space.300', '24px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionWrapper = styled.div({
	margin: `${token('space.200', '16px')} ${token('space.200', '16px')} ${token(
		'space.150',
		'12px',
	)} ${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionHeader = styled(Heading)({
	textTransform: 'uppercase',
	fontSize: '12px',
	lineHeight: '16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionDescription = styled.div({
	fontWeight: 400,
	fontSize: '11px',
	lineHeight: '14px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionDivider = styled.div({
	height: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N30),
	borderRadius: '1px',
	marginBottom: token('space.100', '8px'),
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemWrapper = styled.div({
	maxHeight: '50vh',
	width: '360px',
});

const removeFieldWrapperStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoBackWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon.subtle', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LearnMoreLink = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.accent.blue', colors.B400),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		textDecoration: 'underline',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyFieldWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.accent.blue', colors.B400),
	fontWeight: 600,
	fontSize: '13px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginLeft: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpotlightWrapper = styled.div({
	overflow: 'hidden',
});

// this focus styling does not do anything but reconcile missing focus styles in AKButton possibly related to #hot-103234

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		outlineOffset: '0 !important',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchBarWrapper = styled.div({
	padding: `0px ${token('space.200', '16px')}`,
	height: token('space.500', '40px'),
	marginBottom: token('space.100', '8px'),
});
